/**
 * AuditFilterBar — barra de filtros do explorador (A5).
 *
 * Selects (período/severidade/agente) aplicam on-change; campos de texto
 * (eventType/categoria/entityType/actor) aplicam no submit (Enter ou "Aplicar").
 * Só expõe filtros que o `ListAuditEventsQuery` real aceita — busca-livre,
 * outcomeType e sequence ficam como honest-defer (sem param no backend).
 */

import { useEffect, useState } from 'react';

import { SEVERITY_LABEL, PERIOD_PRESETS } from './audit-labels';
import { AUDIT_SEVERITIES } from './types';

import type { AuditSeverity } from './types';

export interface AuditFilterState {
  period: string;
  severity: AuditSeverity | '';
  agent: 'system' | 'user' | '';
  eventType: string;
  category: string;
  entityType: string;
  // Item 335 — filtro programático por entidade (sem input na barra): seedado via
  // URL (`?entityType=conflict_alert&entityId=<id>`) no salto vindo do Centro de Alertas.
  entityId: string;
  actor: string;
}

export const EMPTY_FILTERS: AuditFilterState = {
  // A-13 (noturna 04/Set/2026): «Todo período» afogava a lista em eventos de depuração.
  period: '24h',
  severity: '',
  agent: '',
  eventType: '',
  category: '',
  entityType: '',
  entityId: '',
  actor: '',
};

interface Props {
  filters: AuditFilterState;
  onChange: (next: AuditFilterState) => void;
}

const SELECT_CLASS =
  'rounded-md border border-border-subtle bg-surface px-2 py-1 text-xs text-text-primary';
const INPUT_CLASS =
  'rounded-md border border-border-subtle bg-surface px-2 py-1 text-xs text-text-primary placeholder:text-text-secondary';

export function AuditFilterBar({ filters, onChange }: Props): JSX.Element {
  const [draft, setDraft] = useState({
    eventType: filters.eventType,
    category: filters.category,
    entityType: filters.entityType,
    actor: filters.actor,
  });

  // Resync quando os filtros forem resetados externamente.
  useEffect(() => {
    setDraft({
      eventType: filters.eventType,
      category: filters.category,
      entityType: filters.entityType,
      actor: filters.actor,
    });
  }, [filters.eventType, filters.category, filters.entityType, filters.actor]);

  function applyText(e: React.FormEvent): void {
    e.preventDefault();
    onChange({ ...filters, ...draft });
  }

  return (
    <form
      onSubmit={applyText}
      className="flex flex-wrap items-center gap-2 border-b border-border-subtle bg-surface px-8 py-3"
      aria-label="Filtros de auditoria"
    >
      <select
        aria-label="Período"
        className={SELECT_CLASS}
        value={filters.period}
        onChange={(e) => onChange({ ...filters, period: e.target.value })}
      >
        {PERIOD_PRESETS.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>

      <select
        aria-label="Severidade"
        className={SELECT_CLASS}
        value={filters.severity}
        onChange={(e) => onChange({ ...filters, severity: e.target.value as AuditSeverity | '' })}
      >
        <option value="">Todas severidades</option>
        {AUDIT_SEVERITIES.map((s) => (
          <option key={s} value={s}>
            {SEVERITY_LABEL[s]}
          </option>
        ))}
      </select>

      <select
        aria-label="Agente"
        className={SELECT_CLASS}
        value={filters.agent}
        onChange={(e) => onChange({ ...filters, agent: e.target.value as 'system' | 'user' | '' })}
      >
        <option value="">Todos agentes</option>
        <option value="system">Sistema</option>
        <option value="user">Usuário</option>
      </select>

      <input
        aria-label="Tipo de evento"
        className={INPUT_CLASS}
        placeholder="nome exato do evento (ex.: conversation.created)"
        value={draft.eventType}
        onChange={(e) => setDraft((d) => ({ ...d, eventType: e.target.value }))}
      />
      <input
        aria-label="Categoria"
        className={INPUT_CLASS}
        placeholder="prefixo (ex.: conductor, lgpd)"
        value={draft.category}
        onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
      />
      <input
        aria-label="Tipo de entidade"
        className={INPUT_CLASS}
        placeholder="tipo da entidade (ex.: conversation, user)"
        value={draft.entityType}
        onChange={(e) => setDraft((d) => ({ ...d, entityType: e.target.value }))}
      />
      <input
        aria-label="Ator"
        className={INPUT_CLASS}
        placeholder="id do usuário que agiu"
        value={draft.actor}
        onChange={(e) => setDraft((d) => ({ ...d, actor: e.target.value }))}
      />

      <button
        type="submit"
        className="rounded-md bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent-hover"
      >
        Aplicar
      </button>
      <button
        type="button"
        className="rounded-md border border-border-subtle px-3 py-1 text-xs text-text-secondary hover:bg-surface-raised"
        onClick={() => onChange(EMPTY_FILTERS)}
      >
        Limpar
      </button>
    </form>
  );
}
